<template>
  <div class="faq-management">
    <v-form ref="faqForm" v-model="formValid" v-if="false">
      <v-card class="mb-4">
        <v-card-title class="pb-0 d-flex justify-space-between align-center">
          <div>
            <h3 class="font-size-20 font-weight-600">
              FAQ Section Information
            </h3>
          </div>

          <!-- Save Section Changes button - now always visible -->
          <div>
            <v-btn color="success" small @click="saveFaqSection" :loading="sectionSaving"
              :disabled="!sectionValid || sectionSaving">
              <v-icon left>mdi-content-save</v-icon>
              Save Section Changes
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500" for="faqTitle">Section Title</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <TextField id="faqTitle" v-model="faqSection.title" :label="'Section Title'" :rules="[
                validateRules.required(faqSection.title, 'Section Title'),
              ]" placeholder="Enter section title" dense filled solo flat class="pt-0 mt-3" @input="onSectionInput" />
            </v-col>
          </v-row>

          <v-row class="mb-2">
            <v-col class="pt-1 my-auto" md="3" sm="12">
              <label class="font-size-16 font-weight-500" for="faqDescription">Description</label>
            </v-col>
            <v-col class="pt-1" md="9" sm="12">
              <TextField id="faqDescription" v-model="faqSection.description" :label="'Description'" :rules="[
                validateRules.required(faqSection.description, 'Description'),
              ]" placeholder="Enter description" dense filled solo flat class="pt-0 mt-3" @input="onSectionInput" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>
    <FAQs type="common" />
  </div>
</template>

<script>
import FAQs from "@/view/pages/partials/FAQs";
import validationMixin from "@/core/plugins/validation-mixin";
import { GET, POST, DELETE } from "@/core/services/store/request.module";
import { mapActions } from "vuex";

export default {
  name: "FAQ",
  components: {
    FAQs
  },
  mixins: [validationMixin],
  props: {
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      loading: false,
      disabled: false,
      faqSection: {
        title: "",
        description: "",
        status: true,
      },

      faqItems: [],

      sectionHasChanges: false,
      sectionSaving: false,
      originalSectionData: {},

      showFaqModal: false,
      faqModalFormValid: false,
      modalSaving: false,
      faqId: null,
      faqData: {
        question: "",
        answer: "",
        status: true,
      },

      itemSaveTimeouts: {},
    };
  },

  computed: {
    sectionValid() {
      return !!this.faqSection.title && !!this.faqSection.description;
    },
  },

  methods: {
    ...mapActions([GET, POST, DELETE]),
    // Helper function to strip HTML tags from text
    stripHtmlTags(html) {
      if (!html) return "";
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || "";
    },

    // Emit success/error messages to parent component
    emitSuccess(message) {
      this.$emit('show-success', message);
    },

    emitError(message) {
      this.$emit('show-error', message);
    },

    async loadFaqData() {
      try {
        this.loading = true;

        const response = await this[GET]({
          url: "/web-setup/faq-data",
        });

        if (response && response.status) {
          const data = response.data;

          this.faqSection = {
            title: data.section?.title || "",
            description: data.section?.description || "",
            status: data.section?.status !== false,
          };

          this.originalSectionData = {
            title: this.faqSection.title,
            description: this.faqSection.description,
            status: this.faqSection.status,
          };

          this.faqItems = data.items || [];
        }
      } catch (error) {
        console.error("Load FAQ data error:", error);
        this.emitError("Failed to load FAQ data");
      } finally {
        this.loading = false;
      }
    },

    onSectionInput() {
      const hasChanges =
        this.faqSection.title !== this.originalSectionData.title ||
        this.faqSection.description !== this.originalSectionData.description ||
        this.faqSection.status !== this.originalSectionData.status;

      this.sectionHasChanges = hasChanges;
    },

    async saveFaqSection() {
      if (!this.sectionValid) {
        this.emitError("Please enter both section title and description before saving.");
        return;
      }

      try {
        this.sectionSaving = true;

        const response = await this[POST]({
          url: "/web-setup/faq-section",
          data: {
            title: this.faqSection.title,
            description: this.faqSection.description,
            status: this.faqSection.status,
          },
        });

        if (response && response.status) {
          this.originalSectionData = {
            title: this.faqSection.title,
            description: this.faqSection.description,
            status: this.faqSection.status,
          };
          this.sectionHasChanges = false;
          this.$emit('show-success', 'FAQ section saved successfully');
          // Success message will be handled by request.module.js watcher
        }
      } catch (error) {
        console.error("Save section error:", error);
        this.emitError("Failed to save FAQ section");
      } finally {
        this.sectionSaving = false;
      }
    },
  },

  mounted() {
    // this.loadFaqData();
  },

  beforeDestroy() {
    Object.values(this.itemSaveTimeouts).forEach((timeout) => {
      clearTimeout(timeout);
    });
  },
};
</script>

<style lang="scss" scoped>
.faq-management {
  padding: 20px;

  .faq-items-list {
    .faq-item-card {
      transition: all 0.2s;
      border: 1px solid #e0e0e0;

      &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }
  }

  .question-display {
    padding: 12px 16px;
    background-color: #f5f5f5;
    border-radius: 4px;
    min-height: 40px;
    border: 1px solid #e0e0e0;
    font-weight: 500;
    font-size: 15px;
    color: #222;
    // line-height: 1.5;       
  }


  .answer-display {
    padding: 12px 16px;
    background-color: #f9f9f9;
    border-radius: 4px;
    min-height: 40px;
    border: 1px solid #e0e0e0;
    // line-height: 1.6;       
    font-size: 15px;
    color: #333;

    ::v-deep {
      p {
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }
      }

      ul,
      ol {
        margin-bottom: 8px;
        padding-left: 20px;
      }

      strong {
        font-weight: 500;
      }

      a {
        color: #1976d2;
        /* make links visible */
        text-decoration: underline;
      }
    }
  }

}
</style>